import { matchesCollectionTitlePrefix } from "./format";
import {
  POCA_VENDOR,
  type Collection,
  type PageInfo,
  type PocaConfig,
  type Product,
  type ProductDetailData,
  type ProductImage,
  type ProductVariant,
  type SortKey,
} from "../types";

type GraphQLResponse<T> = {
  data?: T;
  errors?: Array<{ message: string; locations?: unknown; path?: unknown }>;
};

export class StorefrontError extends Error {
  constructor(message: string, public details?: unknown) {
    super(message);
    this.name = "StorefrontError";
  }
}

async function request<T>(
  config: PocaConfig,
  query: string,
  variables: Record<string, unknown>,
  signal?: AbortSignal
): Promise<T> {
  const url = `https://${config.storefrontDomain}/api/${config.storefrontApiVersion}/graphql.json`;
  const res = await fetch(url, {
    method: "POST",
    signal,
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
      "X-Shopify-Storefront-Access-Token": config.storefrontAccessToken,
    },
    body: JSON.stringify({ query, variables }),
  });

  if (!res.ok) {
    throw new StorefrontError(
      `Storefront API ${res.status} ${res.statusText}`
    );
  }

  const json = (await res.json()) as GraphQLResponse<T>;
  if (json.errors && json.errors.length > 0) {
    throw new StorefrontError(
      json.errors.map((e) => e.message).join("; "),
      json.errors
    );
  }
  if (!json.data) {
    throw new StorefrontError("Empty data from Storefront API");
  }
  return json.data;
}

const PRODUCTS_QUERY = /* GraphQL */ `
  query VendorProducts(
    $query: String!
    $first: Int!
    $after: String
    $sortKey: ProductSortKeys!
    $reverse: Boolean!
    $country: CountryCode
  ) @inContext(country: $country) {
    products(
      query: $query
      first: $first
      after: $after
      sortKey: $sortKey
      reverse: $reverse
    ) {
      pageInfo {
        hasNextPage
        endCursor
      }
      nodes {
        id
        handle
        title
        vendor
        availableForSale
        tags
        featuredImage {
          url
          altText
          width
          height
        }
        images(first: 2) {
          nodes {
            url
            altText
            width
            height
          }
        }
        priceRange {
          minVariantPrice {
            amount
            currencyCode
          }
          maxVariantPrice {
            amount
            currencyCode
          }
        }
        compareAtPriceRange {
          minVariantPrice {
            amount
            currencyCode
          }
          maxVariantPrice {
            amount
            currencyCode
          }
        }
        variants(first: 1) {
          nodes {
            id
            title
            availableForSale
            price {
              amount
              currencyCode
            }
            compareAtPrice {
              amount
              currencyCode
            }
          }
        }
      }
    }
  }
`;

type ProductsResponse = {
  products: {
    pageInfo: PageInfo;
    nodes: Array<
      Omit<Product, "variants" | "images"> & {
        images: { nodes: Product["images"] };
        variants: { nodes: Product["variants"] };
      }
    >;
  };
};

export type FetchProductsParams = {
  search: string;
  minPrice: number | null;
  maxPrice: number | null;
  availableOnly: boolean;
  sortKey: SortKey;
  reverse: boolean;
  first: number;
  after: string | null;
  collectionId: string | null;
  collectionHandle: string | null;
  /** 상품 tag (stageName 등). 예: Kyujin */
  memberTag: string | null;
  /** TYPE 줄에서 고른 종류 태그 (앨범 포카 · POB …). 여러 개면 OR */
  productTags: string[];
};

export type FetchProductsResult = {
  products: Product[];
  pageInfo: PageInfo;
};

/**
 * collectionId 가 있으면 항상 collection.products 로 라우팅한다.
 * products(query) 의 `collection:` qualifier 는 Storefront API 에서 무시되어
 * 컬렉션 경계가 깨지기 때문 (검색·태그 모두 동일한 이슈).
 * 텍스트 검색·멤버 태그·추가 태그는 collection.products 페이지를 추가로 돌며
 * 클라이언트 사이드에서 재필터링한다.
 */
function useProductsSearchQuery(params: FetchProductsParams): boolean {
  if (!params.collectionId) return true;
  return false;
}

function describeProductsFetchStrategy(params: FetchProductsParams) {
  if (params.collectionId && !useProductsSearchQuery(params)) {
    const tags = [params.memberTag?.trim(), ...params.productTags]
      .filter((t): t is string => !!t);
    return {
      api: "collection.products" as const,
      why: tags.length > 0
        ? `group + tag filter(s): ${tags.join(", ")}`
        : "group only — no text search",
    };
  }

  const why: string[] = [];
  if (!params.collectionId) why.push("no group selected");
  if (params.memberTag?.trim()) why.push(`member tag: ${params.memberTag.trim()}`);
  if (params.productTags.length > 0) {
    why.push(`product tags: ${params.productTags.join(", ")}`);
  }
  if (params.search.trim()) why.push(`search: "${params.search.trim()}"`);

  return {
    api: "products(query)" as const,
    why: why.length > 0 ? why.join(" · ") : "products(query) path",
  };
}

function summarizeProductsForLog(products: Product[]) {
  return products.map((p) => ({
    id: p.id,
    handle: p.handle,
    title: p.title,
    availableForSale: p.availableForSale,
    imageCount: p.images.length,
    featuredImage: p.featuredImage?.url ?? null,
    secondImage: p.images[1]?.url ?? null,
    price: p.priceRange.minVariantPrice.amount,
  }));
}

/**
 * Storefront products(query) 검색 문자열.
 * vendor, collection(handle), tag, 검색어·가격·재고 — GraphQL query 만 사용.
 */
function buildQuery(params: FetchProductsParams): string {
  const parts: string[] = [`vendor:'${escapeQueryValue(POCA_VENDOR)}'`];

  if (params.collectionHandle) {
    parts.push(`collection:${escapeQueryValue(params.collectionHandle)}`);
  }

  const memberTag = params.memberTag?.trim();
  if (memberTag) {
    parts.push(`tag:'${escapeQueryValue(memberTag)}'`);
  }

  // 종류 태그는 OR (앨범 포카 "또는" POB)
  const typeTagExprs = params.productTags
    .map((t) => t.trim())
    .filter((t) => t.length > 0)
    .map((t) => `tag:'${escapeQueryValue(t)}'`);
  if (typeTagExprs.length > 0) {
    parts.push(`(${typeTagExprs.join(" OR ")})`);
  }

  const search = params.search.trim();
  if (search) {
    const tokens = search.split(/\s+/).map((t) => escapeQueryValue(t));
    parts.push(tokens.map((t) => `(${t}* OR title:${t}*)`).join(" AND "));
  }

  if (params.minPrice != null && params.minPrice >= 0) {
    parts.push(`variants.price:>=${params.minPrice}`);
  }
  if (params.maxPrice != null && params.maxPrice >= 0) {
    parts.push(`variants.price:<=${params.maxPrice}`);
  }

  if (params.availableOnly) {
    parts.push("available_for_sale:true");
  }

  return parts.join(" AND ");
}

function escapeQueryValue(value: string): string {
  return value.replace(/(["'\\])/g, "\\$1");
}

export async function fetchVendorProducts(
  config: PocaConfig,
  params: FetchProductsParams,
  signal?: AbortSignal
): Promise<FetchProductsResult> {
  const strategy = describeProductsFetchStrategy(params);

  console.groupCollapsed(
    `[poca] fetch products → ${strategy.api} (${params.after ? "append" : "reset"})`
  );
  console.log("strategy", strategy);
  console.log("params", {
    vendor: POCA_VENDOR,
    shop: config.storefrontDomain,
    collectionId: params.collectionId,
    collectionHandle: params.collectionHandle,
    memberTag: params.memberTag?.trim() ?? null,
    productTags: params.productTags,
    search: params.search.trim() || null,
    minPrice: params.minPrice,
    maxPrice: params.maxPrice,
    availableOnly: params.availableOnly,
    sortKey: params.sortKey,
    reverse: params.reverse,
    first: params.first,
    after: params.after,
  });

  if (params.collectionId && !useProductsSearchQuery(params)) {
    const result = await fetchCollectionProducts(config, params, signal);
    console.log(`received ${result.products.length} product(s)`, {
      pageInfo: result.pageInfo,
      products: summarizeProductsForLog(result.products),
    });
    console.groupEnd();
    return result;
  }

  const query = buildQuery(params);
  const variables = {
    query,
    first: params.first,
    after: params.after,
    sortKey: params.sortKey,
    reverse: params.reverse,
    country: config.countryCode || undefined,
  };

  console.log("graphql", {
    operation: "VendorProducts",
    variables,
  });

  const data = await request<ProductsResponse>(
    config,
    PRODUCTS_QUERY,
    variables,
    signal
  );

  const products = data.products.nodes.map((n) => ({
    ...n,
    images: n.images.nodes,
    variants: n.variants.nodes,
  }));

  console.log(`received ${products.length} product(s)`, {
    pageInfo: data.products.pageInfo,
    products: summarizeProductsForLog(products),
  });
  console.groupEnd();

  return { pageInfo: data.products.pageInfo, products };
}

/* ------------------------------------------------------------------ */
/* Collection.products — 컬렉션만 선택 시 (멤버 tag 없을 때)              */
/* ------------------------------------------------------------------ */

const COLLECTION_PRODUCTS_QUERY = /* GraphQL */ `
  query CollectionProducts(
    $id: ID!
    $first: Int!
    $after: String
    $filters: [ProductFilter!]
    $sortKey: ProductCollectionSortKeys!
    $reverse: Boolean!
    $country: CountryCode
  ) @inContext(country: $country) {
    collection(id: $id) {
      id
      handle
      title
      products(
        first: $first
        after: $after
        filters: $filters
        sortKey: $sortKey
        reverse: $reverse
      ) {
        pageInfo { hasNextPage endCursor }
        nodes {
          id
          handle
          title
          vendor
          availableForSale
          tags
          featuredImage {
            url
            altText
            width
            height
          }
          images(first: 2) {
            nodes {
              url
              altText
              width
              height
            }
          }
          priceRange {
            minVariantPrice { amount currencyCode }
            maxVariantPrice { amount currencyCode }
          }
          compareAtPriceRange {
            minVariantPrice { amount currencyCode }
            maxVariantPrice { amount currencyCode }
          }
          variants(first: 1) {
            nodes {
              id
              title
              availableForSale
              price { amount currencyCode }
              compareAtPrice { amount currencyCode }
            }
          }
        }
      }
    }
  }
`;

type CollectionProductsResponse = {
  collection: {
    products: {
      pageInfo: PageInfo;
      nodes: Array<
        Omit<Product, "variants" | "images"> & {
          images: { nodes: Product["images"] };
          variants: { nodes: Product["variants"] };
        }
      >;
    };
  } | null;
};

type CollectionSortKey =
  | "MANUAL"
  | "BEST_SELLING"
  | "CREATED"
  | "ID"
  | "PRICE"
  | "RELEVANCE"
  | "TITLE"
  | "COLLECTION_DEFAULT";

function mapToCollectionSortKey(key: SortKey): CollectionSortKey {
  switch (key) {
    case "RELEVANCE":
      return "RELEVANCE";
    case "BEST_SELLING":
      return "BEST_SELLING";
    case "CREATED_AT":
      return "CREATED";
    case "PRICE":
      return "PRICE";
    case "TITLE":
      return "TITLE";
    default:
      return "COLLECTION_DEFAULT";
  }
}

const COLLECTION_FETCH_BATCH_SIZE = 100;
const COLLECTION_MAX_FETCH_PAGES = 10;

/** 컬렉션 소속 상품만 — collection(id).products + vendor/tag/가격/재고 필터. */
async function fetchCollectionProducts(
  config: PocaConfig,
  params: FetchProductsParams,
  signal?: AbortSignal
): Promise<FetchProductsResult> {
  const memberTag = params.memberTag?.trim() ?? "";
  const productTags = params.productTags
    .map((t) => t.trim())
    .filter((t) => t.length > 0);
  const searchTokens = params.search
    .trim()
    .toLowerCase()
    .split(/\s+/)
    .filter((t) => t.length > 0);
  const needsClientFilter =
    !!memberTag ||
    productTags.length > 0 ||
    searchTokens.length > 0 ||
    params.availableOnly;

  const filters: Array<Record<string, unknown>> = [
    { productVendor: POCA_VENDOR },
  ];
  if (memberTag) filters.push({ tag: memberTag });
  // 종류 태그(OR)는 ProductFilter 로 표현이 안 되므로(여러 tag 필터 = AND) 클라이언트에서 거른다
  if (params.minPrice != null || params.maxPrice != null) {
    filters.push({
      price: {
        min: params.minPrice ?? 0,
        ...(params.maxPrice != null ? { max: params.maxPrice } : {}),
      },
    });
  }
  if (params.availableOnly) {
    filters.push({ available: true });
  }

  const targetCount = params.first;
  const fetchBatchSize = needsClientFilter
    ? COLLECTION_FETCH_BATCH_SIZE
    : params.first;
  const maxPages = needsClientFilter ? COLLECTION_MAX_FETCH_PAGES : 1;

  const collected: Product[] = [];
  let cursor: string | null = params.after;
  let hasNextPage = true;
  let pagesFetched = 0;

  while (
    hasNextPage &&
    collected.length < targetCount &&
    pagesFetched < maxPages
  ) {
    const variables = {
      id: params.collectionId,
      first: fetchBatchSize,
      after: cursor,
      filters,
      sortKey: mapToCollectionSortKey(params.sortKey),
      reverse: params.reverse,
      country: config.countryCode || undefined,
    };

    console.log("graphql", {
      operation: "CollectionProducts",
      page: pagesFetched + 1,
      variables,
    });

    const data = await request<CollectionProductsResponse>(
      config,
      COLLECTION_PRODUCTS_QUERY,
      variables,
      signal
    );
    pagesFetched++;

    if (!data.collection) {
      console.warn("collection not found", {
        collectionId: params.collectionId,
        collectionHandle: params.collectionHandle,
      });
      return {
        pageInfo: { hasNextPage: false, endCursor: null },
        products: [],
      };
    }

    let pageProducts = data.collection.products.nodes.map((n) => ({
      ...n,
      images: n.images.nodes,
      variants: n.variants.nodes,
    }));

    // Storefront ProductFilter { tag } / { available } 는 스토어 설정에 따라
    // 무시될 수 있으므로 클라이언트 사이드에서 재필터링 (확실한 보장).
    if (memberTag) {
      const lower = memberTag.toLowerCase();
      pageProducts = pageProducts.filter((p) =>
        p.tags.some((t) => t.toLowerCase() === lower)
      );
    }
    if (productTags.length > 0) {
      const wanted = new Set(productTags.map((t) => t.toLowerCase()));
      pageProducts = pageProducts.filter((p) =>
        p.tags.some((t) => wanted.has(t.toLowerCase()))
      );
    }
    if (params.availableOnly) {
      pageProducts = pageProducts.filter((p) => p.availableForSale);
    }

    if (searchTokens.length > 0) {
      pageProducts = pageProducts.filter((p) => {
        const haystack = `${p.title} ${p.handle} ${p.tags.join(" ")}`.toLowerCase();
        return searchTokens.every((tok) => haystack.includes(tok));
      });
    }

    collected.push(...pageProducts);
    cursor = data.collection.products.pageInfo.endCursor;
    hasNextPage = data.collection.products.pageInfo.hasNextPage;
  }

  return {
    pageInfo: { hasNextPage, endCursor: cursor },
    products: collected,
  };
}

/* ------------------------------------------------------------------ */
/* Scope product tags — TYPE 줄 건수용 (tags 만, 가볍게)                 */
/* ------------------------------------------------------------------ */

export type ScopeProductTagsParams = {
  collectionId: string | null;
  availableOnly: boolean;
};

const SCOPE_TAGS_PAGE_SIZE = 250;
/** 250 × 30 = 7,500 상품까지 (재고 있는 PC 상품이 5,700개쯤). 넘으면 건수가 조금 모자랄 뿐 */
const SCOPE_TAGS_MAX_PAGES = 30;

const SCOPE_TAGS_ALL_QUERY = /* GraphQL */ `
  query ScopeTagsAll($query: String!, $first: Int!, $after: String) {
    products(query: $query, first: $first, after: $after) {
      pageInfo { hasNextPage endCursor }
      nodes { tags }
    }
  }
`;

const SCOPE_TAGS_COLLECTION_QUERY = /* GraphQL */ `
  query ScopeTagsCollection(
    $id: ID!
    $first: Int!
    $after: String
    $filters: [ProductFilter!]
  ) {
    collection(id: $id) {
      products(first: $first, after: $after, filters: $filters) {
        pageInfo { hasNextPage endCursor }
        nodes { tags availableForSale vendor }
      }
    }
  }
`;

type ScopeTagsAllResponse = {
  products: { pageInfo: PageInfo; nodes: Array<{ tags: string[] }> };
};
type ScopeTagsCollectionResponse = {
  collection: {
    products: {
      pageInfo: PageInfo;
      nodes: Array<{ tags: string[]; availableForSale: boolean; vendor: string }>;
    };
  } | null;
};

/**
 * 범위(전체 또는 컬렉션) 안 상품의 tags 만 페이지 돌며 모은다.
 * 제목·이미지·가격은 안 받아 한 페이지(250개)가 0.4초쯤. 페이지마다 onPage 로 흘려 보낸다.
 */
export async function fetchScopeProductTags(
  config: PocaConfig,
  params: ScopeProductTagsParams,
  signal?: AbortSignal,
  onPage?: (pageNodes: Array<{ tags: string[] }>) => void
): Promise<Array<{ tags: string[] }>> {
  const out: Array<{ tags: string[] }> = [];
  let after: string | null = null;

  for (let page = 0; page < SCOPE_TAGS_MAX_PAGES; page++) {
    let pageInfo: PageInfo;
    const pageNodes: Array<{ tags: string[] }> = [];

    if (params.collectionId) {
      const filters: Array<Record<string, unknown>> = [
        { productVendor: POCA_VENDOR },
      ];
      if (params.availableOnly) filters.push({ available: true });
      const data: ScopeTagsCollectionResponse = await request(
        config,
        SCOPE_TAGS_COLLECTION_QUERY,
        { id: params.collectionId, first: SCOPE_TAGS_PAGE_SIZE, after, filters },
        signal
      );
      if (!data.collection) break;
      // ProductFilter 가 스토어 설정에 따라 무시될 수 있어 한 번 더 거른다
      for (const n of data.collection.products.nodes) {
        if (n.vendor !== POCA_VENDOR) continue;
        if (params.availableOnly && !n.availableForSale) continue;
        pageNodes.push({ tags: n.tags });
      }
      pageInfo = data.collection.products.pageInfo;
    } else {
      const query =
        `vendor:'${escapeQueryValue(POCA_VENDOR)}'` +
        (params.availableOnly ? " AND available_for_sale:true" : "");
      const data: ScopeTagsAllResponse = await request(
        config,
        SCOPE_TAGS_ALL_QUERY,
        { query, first: SCOPE_TAGS_PAGE_SIZE, after },
        signal
      );
      pageNodes.push(...data.products.nodes);
      pageInfo = data.products.pageInfo;
    }

    out.push(...pageNodes);
    if (pageNodes.length > 0 && pageInfo.hasNextPage) onPage?.(pageNodes);
    if (!pageInfo.hasNextPage) break;
    after = pageInfo.endCursor;
  }

  return out;
}

/* ------------------------------------------------------------------ */
/* Prefixed collections (title prefix, no vendor filter)               */
/* ------------------------------------------------------------------ */

const COLLECTIONS_PAGE_SIZE = 250;
/** 스토어 컬렉션 스캔 상한 (250 × N). 600+ PC_ 컬렉션 대비 여유 있게. */
const MAX_COLLECTION_PAGES = 50;

const COLLECTIONS_BY_TITLE_QUERY = /* GraphQL */ `
  query CollectionsByTitle($first: Int!, $after: String, $query: String!) {
    collections(first: $first, after: $after, sortKey: TITLE, query: $query) {
      pageInfo {
        hasNextPage
        endCursor
      }
      nodes {
        id
        handle
        title
        image { url altText }
        products(first: 1, sortKey: CREATED, reverse: true) {
          nodes { id createdAt }
        }
      }
    }
  }
`;

type CollectionsByTitleResponse = {
  collections: {
    pageInfo: PageInfo;
    nodes: Array<
      Collection & {
        products: { nodes: Array<{ id: string; createdAt: string }> };
      }
    >;
  };
};

function isNonEmptyCollection(
  c: Collection & { products: { nodes: Array<{ id: string; createdAt: string }> } }
): boolean {
  return c.products.nodes.length > 0;
}

/** Storefront collections(query) 에 넣을 title prefix 검색어 후보. */
function buildCollectionSearchQueries(prefix: string): string[] {
  if (!prefix) return [];
  const escaped = escapeQueryValue(prefix);
  const alpha = prefix.match(/^[A-Za-z0-9]+/)?.[0] ?? "";
  const queries = [`title:${escaped}*`, `title:"${escaped}"*`];
  if (alpha && alpha !== escaped) {
    queries.push(`title:${alpha}*`);
  }
  return [...new Set(queries)];
}

/**
 * title prefix(예: PC_) 로 시작하는 컬렉션만 Storefront search 로 조회.
 * 상품이 0개인 빈 컬렉션은 제외. vendor 필터 없음.
 */
export async function fetchVendorCollections(
  config: PocaConfig,
  signal?: AbortSignal
): Promise<Collection[]> {
  const prefix = config.collectionTitlePrefix ?? "PC_";
  const searchQueries = buildCollectionSearchQueries(prefix);

  // console.group("[poca] fetchVendorCollections — start");
  // console.log("config", {
  //   prefix,
  //   searchQueries,
  //   storefrontDomain: config.storefrontDomain,
  //   apiVersion: config.storefrontApiVersion,
  // });

  if (searchQueries.length === 0) {
    console.warn("[poca] empty collectionTitlePrefix — nothing to fetch");
    console.groupEnd();
    return [];
  }

  for (const apiQuery of searchQueries) {
    console.log(`[poca] trying collections query: ${apiQuery}`);
    const matched = await paginatePrefixedCollections(
      config,
      prefix,
      apiQuery,
      signal
    );
    if (matched.length > 0) {
      // console.log("[poca] fetchVendorCollections — done", {
      //   apiQuery,
      //   matchedCount: matched.length,
      //   first: matched[0]?.title ?? null,
      //   last: matched[matched.length - 1]?.title ?? null,
      // });
      if (matched.length <= 30) {
        console.log("[poca] all matched titles", matched.map((c) => c.title));
      } else {
        console.log("[poca] matched titles sample", {
          first10: matched.slice(0, 10).map((c) => c.title),
          last10: matched.slice(-10).map((c) => c.title),
        });
      }
      console.groupEnd();
      return matched;
    }
    console.warn(`[poca] query "${apiQuery}" returned 0 matches — trying next`);
  }

  console.error(
    `[poca] no collections matched prefix "${prefix}" with any search query`,
    searchQueries
  );
  console.groupEnd();
  return [];
}

async function paginatePrefixedCollections(
  config: PocaConfig,
  prefix: string,
  apiQuery: string,
  signal?: AbortSignal
): Promise<Collection[]> {
  const matched: Collection[] = [];
  let after: string | null = null;
  let page = 0;
  let totalReturned = 0;
  let emptyExcluded = 0;

  while (page < MAX_COLLECTION_PAGES) {
    // console.log(`[poca] collections page ${page + 1} — request`, {
    //   apiQuery,
    //   first: COLLECTIONS_PAGE_SIZE,
    //   after,
    // });

    const data: CollectionsByTitleResponse = await request(
      config,
      COLLECTIONS_BY_TITLE_QUERY,
      { first: COLLECTIONS_PAGE_SIZE, after, query: apiQuery },
      signal
    );

    const nodes = data.collections.nodes;
    totalReturned += nodes.length;

    const pageMatched: Collection[] = [];
    const pageSkipped: string[] = [];
    const pageEmpty: string[] = [];

    for (const c of nodes) {
      if (!matchesCollectionTitlePrefix(c.title, prefix)) {
        pageSkipped.push(c.title);
        continue;
      }
      if (!isNonEmptyCollection(c)) {
        emptyExcluded++;
        pageEmpty.push(c.title);
        continue;
      }
      const item: Collection = {
        id: c.id,
        handle: c.handle,
        title: c.title,
        image: c.image,
        latestProductCreatedAt: c.products.nodes[0]?.createdAt ?? null,
      };
      matched.push(item);
      pageMatched.push(item);
    }

    // console.log(`[poca] collections page ${pageIndex} — response`, {
    //   apiQuery,
    //   returned: nodes.length,
    //   matchedThisPage: pageMatched.length,
    //   matchedTotal: matched.length,
    //   skippedThisPage: pageSkipped.length,
    //   emptyExcludedThisPage: pageEmpty.length,
    //   hasNextPage: data.collections.pageInfo.hasNextPage,
    // });

    // if (pageMatched.length > 0) {
    //   console.log(
    //     `[poca] collections page ${pageIndex} — matched`,
    //     pageMatched.map((c) => c.title)
    //   );
    // }
    // if (pageSkipped.length > 0) {
    //   console.warn(
    //     `[poca] collections page ${pageIndex} — API returned non-matching titles (first 5)`,
    //     pageSkipped.slice(0, 5)
    //   );
    // }
    // if (pageEmpty.length > 0) {
    //   console.log(
    //     `[poca] collections page ${pageIndex} — empty (no products)`,
    //     pageEmpty.length <= 10 ? pageEmpty : pageEmpty.slice(0, 10)
    //   );
    // }

    if (!data.collections.pageInfo.hasNextPage) break;
    after = data.collections.pageInfo.endCursor;
    page++;
  }

  if (page + 1 >= MAX_COLLECTION_PAGES) {
    console.warn(
      `[poca] hit MAX_COLLECTION_PAGES (${MAX_COLLECTION_PAGES}); results may be incomplete`
    );
  }

  console.log("[poca] paginate summary", {
    apiQuery,
    pagesFetched: page + 1,
    totalReturned,
    matchedCount: matched.length,
    emptyExcludedTotal: emptyExcluded,
  });

  matched.sort((a, b) =>
    a.title.localeCompare(b.title, undefined, { sensitivity: "base" })
  );
  return matched;
}

/* ------------------------------------------------------------------ */
/* Single product by handle                                            */
/* ------------------------------------------------------------------ */

const PRODUCT_BY_HANDLE_QUERY = /* GraphQL */ `
  query ProductByHandle(
    $handle: String!
    $country: CountryCode
  ) @inContext(country: $country) {
    product(handle: $handle) {
      id
      handle
      title
      vendor
      availableForSale
      tags
      descriptionHtml
      featuredImage {
        url
        altText
        width
        height
      }
      images(first: 20) {
        nodes {
          url
          altText
          width
          height
        }
      }
      priceRange {
        minVariantPrice { amount currencyCode }
        maxVariantPrice { amount currencyCode }
      }
      compareAtPriceRange {
        minVariantPrice { amount currencyCode }
        maxVariantPrice { amount currencyCode }
      }
      variants(first: 100) {
        nodes {
          id
          title
          availableForSale
          price { amount currencyCode }
          compareAtPrice { amount currencyCode }
        }
      }
    }
  }
`;

type ProductByHandleResponse = {
  product:
    | (Omit<ProductDetailData, "images" | "variants"> & {
        images: { nodes: ProductImage[] };
        variants: { nodes: ProductVariant[] };
      })
    | null;
};

export async function fetchProductByHandle(
  config: PocaConfig,
  handle: string,
  signal?: AbortSignal
): Promise<ProductDetailData | null> {
  const data = await request<ProductByHandleResponse>(
    config,
    PRODUCT_BY_HANDLE_QUERY,
    {
      handle,
      country: config.countryCode || undefined,
    },
    signal
  );

  if (!data.product) return null;

  return {
    ...data.product,
    images: data.product.images.nodes,
    variants: data.product.variants.nodes,
  };
}

/* ------------------------------------------------------------------ */
/* Related products ("Recommended for you")                            */
/* ------------------------------------------------------------------ */

/**
 * 추천 매칭에서 제외하는 범용 태그(거의 모든 포토카드가 공유 → 변별력 없음).
 * 소문자로 비교한다. 멤버·그룹·아티스트·앨범 태그만 남겨 같은 "최애" 위주로 추천.
 */
const RELATED_GENERIC_TAGS = new Set([
  "pc",
  "poca",
  "photocard",
  "photo card",
  "photocards",
  "official",
  "broadcast",
  "polaroid",
  "poloaroid",
  "postcard",
  "random",
  "trading",
  "pre-order",
  "preorder",
  "pre order",
  "md",
  "new",
  "new items",
  "sale",
  "in stock",
  "signed",
  "kpop",
  "k-pop",
]);

/** 범용 태그·빈 값·중복 제거 후 변별력 있는 태그만 추출. */
function meaningfulRelatedTags(tags: string[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const raw of tags) {
    const trimmed = raw.trim();
    if (trimmed.length < 2) continue;
    const lower = trimmed.toLowerCase();
    if (RELATED_GENERIC_TAGS.has(lower)) continue;
    if (seen.has(lower)) continue;
    seen.add(lower);
    out.push(trimmed);
  }
  return out;
}

/**
 * 현재 상품과 태그가 겹치는 같은 vendor(PC)·판매중 상품을 추천.
 * - 의미있는 태그(멤버/그룹/앨범)로 OR 검색 → 자기 자신 제외
 * - 공유 태그 수(overlap) 내림차순으로 재정렬해 같은 "최애"를 우선 노출
 */
export async function fetchRelatedProducts(
  config: PocaConfig,
  base: { handle: string; tags: string[] },
  limit: number,
  signal?: AbortSignal
): Promise<Product[]> {
  const tags = meaningfulRelatedTags(base.tags).slice(0, 10);
  if (tags.length === 0) return [];

  const tagExpr = tags.map((t) => `tag:'${escapeQueryValue(t)}'`).join(" OR ");
  const query =
    `vendor:'${escapeQueryValue(POCA_VENDOR)}'` +
    ` AND available_for_sale:true AND (${tagExpr})`;

  const variables = {
    query,
    first: Math.max(limit * 3, 24),
    after: null,
    sortKey: "BEST_SELLING" as const,
    reverse: false,
    country: config.countryCode || undefined,
  };

  const data = await request<ProductsResponse>(
    config,
    PRODUCTS_QUERY,
    variables,
    signal
  );

  const wanted = new Set(tags.map((t) => t.toLowerCase()));

  return data.products.nodes
    .map((n) => ({
      ...n,
      images: n.images.nodes,
      variants: n.variants.nodes,
    }))
    .filter((p) => p.handle !== base.handle)
    .map((p) => ({
      product: p,
      overlap: p.tags.reduce(
        (acc, t) => acc + (wanted.has(t.toLowerCase()) ? 1 : 0),
        0
      ),
    }))
    .filter((s) => s.overlap > 0)
    .sort((a, b) => b.overlap - a.overlap)
    .slice(0, limit)
    .map((s) => s.product);
}
